<template>
  <v-container fluid class="erp-page page-container">
    <div class="erp-page-header header-section mb-4">
      <div class="d-flex align-center">
        <v-icon color="primary" class="mr-3" size="32">mdi-cart-arrow-down</v-icon>
        <div>
          <h1 class="erp-page-title d-flex align-center mb-1" style="line-height: 1.2;">Compras <HelpHint text="Gestiona ordenes de compra, confirmación y recepción. Si una compra ya fue contabilizada, no puede editarse directamente." class="ml-2" /></h1>
          <p class="text-body-2 grey--text text--darken-1 mb-0">Gestión de abastecimiento y recepción de mercadería</p>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" elevation="2" @click="createNewOrder" class="rounded">
        <v-icon left small>mdi-plus</v-icon> Nueva Orden
      </v-btn>
    </div>

    <v-row>
      <v-col cols="12">
        <v-card elevation="2" class="rounded-xl overflow-hidden mb-6">
          <v-data-table
            :headers="headers"
            :items="orders"
            :loading="loading"
            class="custom-table"
            @click:row="selectOrder"
          >
            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item.status)" small label dark class="font-weight-bold">
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.total="{ item }">
              <span class="font-weight-bold">$ {{ formatNumber(item.total) }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

    </v-row>

    <!-- Dialogo para Detalles de Orden -->
    <v-dialog v-model="viewOrderDialog" max-width="550">
      <v-card v-if="selectedOrder" class="rounded-xl detail-card">
        <v-card-title class="text-h6 primary white--text py-3">
          Orden: {{ selectedOrder.docNumber }}
          <v-spacer></v-spacer>
          <v-chip color="white" light small class="font-weight-black mr-4">{{ getStatusText(selectedOrder.status).toUpperCase() }}</v-chip>
          <v-btn icon dark @click="viewOrderDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" sm="6">
              <div class="mb-2">
                <div class="text-caption grey--text font-weight-bold">PROVEEDOR</div>
                <div class="text-subtitle-1 font-weight-bold">{{ selectedOrder.supplierAccount?.name || '—' }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="mb-2">
                <div class="text-caption grey--text font-weight-bold">CREADA POR</div>
                <div class="text-body-2 font-weight-medium">{{ selectedOrder.createdBy || 'Sistema' }}</div>
                <div class="text-caption grey--text">{{ formatDate(selectedOrder.createdAt || selectedOrder.issueDate) }}</div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-2"></v-divider>

          <div class="mb-4">
            <div class="text-caption grey--text font-weight-bold mb-1">ÍTEMS</div>
            <div class="border rounded px-3 py-1 bg-light-grey" style="border: 1px solid #e0e0e0; background-color: #f9f9f9;">
              <div v-for="(item, index) in selectedOrder.items" :key="item.id || index" class="d-flex justify-space-between align-center py-2" :style="index < selectedOrder.items.length - 1 ? 'border-bottom: 1px solid #e0e0e0;' : ''">
                <div>
                  <div class="text-body-2 font-weight-bold">{{ item.product?.name || getProductName(item.productId) }}</div>
                  <div class="text-caption grey--text">{{ item.quantity }} x $ {{ formatNumber(item.unitCost) }}</div>
                </div>
                <div class="font-weight-black text-body-2">$ {{ formatNumber(item.lineTotal) }}</div>
              </div>
              <div v-if="!selectedOrder.items || selectedOrder.items.length === 0" class="text-center py-3 grey--text text-caption">
                <v-progress-circular indeterminate color="primary" size="16" class="mr-2" width="2"></v-progress-circular>
                Cargando ítems...
              </div>
            </div>
          </div>

          <div class="d-flex justify-space-between align-center py-2 px-3 rounded mb-4" style="background-color: #f0f4f8;">
            <span class="text-subtitle-1 font-weight-black grey--text text--darken-2">TOTAL</span>
            <span class="text-h5 font-weight-black primary--text">$ {{ formatNumber(selectedOrder.total) }}</span>
          </div>

          <!-- Workflow Buttons -->
          <div class="d-flex flex-column" style="gap: 8px">
            <v-row dense>
              <v-col cols="6">
                <v-btn color="error" outlined block elevation="0" @click="cancelOrder(selectedOrder.id)" :loading="cancelling" :disabled="selectedOrder.status !== 'draft'">
                  <v-icon left small>mdi-delete</v-icon> Cancelar
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn color="secondary" block elevation="1" @click="editOrder(selectedOrder)" :disabled="selectedOrder.status !== 'draft'">
                  <v-icon left small>mdi-pencil</v-icon> Editar
                </v-btn>
              </v-col>
            </v-row>
            <div v-if="selectedOrder.status !== 'draft'" class="text-caption text-center grey--text mb-2" style="line-height: 1.2">
              Solo las órdenes en Borrador se pueden editar o cancelar.
            </div>

            <v-btn
              v-if="selectedOrder.status === 'draft'"
              color="success"
              block
              elevation="1"
              @click="confirmOrder(selectedOrder.id)"
              :loading="confirming"
            >
              <v-icon left>mdi-check-circle</v-icon> Confirmar Pedido
            </v-btn>

            <v-btn
              v-if="selectedOrder.status === 'purchase' || selectedOrder.status === 'partial'"
              color="info"
              block
              elevation="1"
              @click="openReceiveDialog(selectedOrder)"
            >
              <v-icon left>mdi-package-variant-closed</v-icon> Recibir Productos
            </v-btn>

            <v-btn
              v-if="selectedOrder.status === 'done' || selectedOrder.status === 'partial'"
              color="primary"
              outlined
              block
              @click="createBill(selectedOrder.id)"
            >
              <v-icon left>mdi-file-document-outline</v-icon> Ingresar Factura de Proveedor
            </v-btn>

            <v-btn
              v-if="selectedOrder.status === 'invoiced'"
              color="success"
              outlined
              block
              @click="openPayDialog(selectedOrder)"
            >
              <v-icon left>mdi-cash-check</v-icon> Registrar Pago Proveedor
            </v-btn>

            <v-btn
              v-if="selectedOrder.hasAttachment"
              color="indigo"
              outlined
              block
              class="mt-2"
              @click="viewAttachment(selectedOrder.id)"
            >
              <v-icon left>mdi-paperclip</v-icon> Ver Comprobante de Compra
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialogo para Nueva Orden -->
    <v-dialog v-model="dialog" max-width="700" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6 primary white--text py-3">
          Nueva Orden de Compra
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="newOrder.supplierId"
                :items="suppliers"
                item-text="name"
                item-value="id"
                label="Proveedor"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-account-search"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="newOrder.warehouseId"
                :items="warehouses"
                item-text="name"
                item-value="id"
                label="Recibir en Depósito"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-warehouse"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <div class="d-flex align-center mb-2">
            <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Agregar Productos</h3>
          </div>

          <v-row dense align="center">
            <v-col cols="12" md="5">
              <v-autocomplete
                v-model="itemForm.productId"
                :items="filteredProducts"
                :loading="loadingProducts"
                :disabled="!newOrder.supplierId"
                item-text="name"
                item-value="id"
                :placeholder="newOrder.supplierId ? 'Seleccione un producto' : 'Primero seleccione un proveedor'"
                label="Producto"
                outlined
                hide-details
                dense
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                v-model.number="itemForm.quantity"
                label="Cant."
                type="number"
                min="1"
                outlined
                hide-details
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model.number="itemForm.unitCost"
                label="Costo Unit."
                type="number"
                min="0.01"
                step="0.01"
                prefix="$"
                outlined
                hide-details
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn color="secondary" block class="rounded" @click="addItem">
                Añadir
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="itemHeaders"
            :items="newOrder.items"
            hide-default-footer
            dense
            class="mt-2 border rounded"
          >
            <template v-slot:item.productName="{ item }">
              {{ item.productName || getProductName(item.productId) }}
            </template>
            <template v-slot:item.lineTotal="{ item }">
              $ {{ formatNumber(item.quantity * item.unitCost) }}
            </template>
            <template v-slot:item.actions="{ index }">
              <v-btn icon color="error" small @click="newOrder.items.splice(index, 1)">
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <div class="d-flex justify-end mt-3 px-2">
            <div class="text-h6 font-weight-black primary--text">
              Total: $ {{ formatNumber(newOrderTotal) }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 bg-light-grey">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="dialog = false" class="mr-2">Cancelar</v-btn>
          <v-btn color="primary" class="px-6 rounded" @click="saveOrder" :loading="saving" :disabled="!isOrderValid">
            Guardar Borrador
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo para Recepcion de Mercadería -->
    <v-dialog v-model="receiveDialog" max-width="700" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="headline primary white--text py-4">
          Recepción de Mercadería
          <v-spacer></v-spacer>
          <v-btn icon dark @click="receiveDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-6">
          <p class="mb-4">Modifique las cantidades realmente recibidas para cada producto.</p>
          <v-data-table
            :headers="[
              { text: 'Producto', value: 'productName' },
              { text: 'Comprada', value: 'orderedQty', align: 'center' },
              { text: 'Ya recibido', value: 'alreadyReceivedQty', align: 'center' },
              { text: 'Pendiente', value: 'pendingQty', align: 'center' },
              { text: 'A recibir ahora', value: 'receivedQty', align: 'center' }
            ]"
            :items="receiveFormItems"
            hide-default-footer
            dense
            class="mt-2 border rounded-lg"
          >
            <template v-slot:item.receivedQty="{ item }">
              <v-text-field
                v-model.number="item.receivedQty"
                type="number"
                min="0"
                :max="item.pendingQty"
                outlined
                dense
                hide-details
                style="max-width: 120px"
              ></v-text-field>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-text v-if="hasReceiveShortage" class="pt-0">
          <v-alert dense text type="warning" class="mb-3">
            Hay cantidades pendientes. Indicá el motivo; se generará una orden interna por el faltante.
          </v-alert>
          <v-textarea
            v-model="receivePartialReason"
            label="Motivo de recepción parcial"
            outlined
            dense
            rows="2"
            :rules="[v => !hasReceiveShortage || !!String(v || '').trim() || 'El motivo es obligatorio']"
          />
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="receiveDialog = false" class="mr-2">Cancelar</v-btn>
          <v-btn color="primary" x-large class="px-8 rounded-lg" @click="confirmReceiveProducts" :loading="receiving">
            Confirmar Recepción
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo para Registrar Factura de Proveedor (Facturación y Pago) -->
    <v-dialog v-model="billDialog" max-width="500" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="headline teal white--text py-4">
          <v-icon left dark class="mr-2">mdi-file-document-outline</v-icon>
          Ingresar Factura del Proveedor
          <v-spacer></v-spacer>
          <v-btn icon dark @click="billDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-6">
          <p class="body-2 grey--text text--darken-1 mb-4">
            Ingrese los datos de la factura provista por su proveedor. Al guardarla, se asentará el gasto y crédito fiscal en su contabilidad, y se registrará la deuda en la cuenta corriente del proveedor.
          </p>
          <v-form ref="billForm" lazy-validation>
            <v-select
              v-model="billForm.invoiceType"
              :items="['A', 'B', 'C', 'INTERNAL']"
              label="Tipo de Factura"
              outlined
              dense
              required
            ></v-select>
            <v-text-field
              v-model="billForm.supplierDocNumber"
              label="Número de Factura de Proveedor"
              placeholder="Ej: 0001-00012345"
              outlined
              dense
              required
              :rules="[v => !!v || 'El número de factura es requerido']"
            ></v-text-field>
            <v-text-field
              v-model="billForm.issueDate"
              type="date"
              label="Fecha de Factura"
              outlined
              dense
              required
            ></v-text-field>
            <v-textarea
              v-model="billForm.notes"
              label="Notas / Observaciones"
              outlined
              dense
              rows="3"
            ></v-textarea>
            <v-file-input
              v-model="billForm.attachment"
              accept="image/*, .pdf"
              label="Comprobante Adjunto (Opcional)"
              placeholder="Seleccione un PDF o Imagen"
              prepend-icon="mdi-paperclip"
              outlined
              dense
              show-size
            ></v-file-input>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="billDialog = false" class="mr-2">Cancelar</v-btn>
          <v-btn color="teal" dark class="px-6 rounded-lg font-weight-bold" :loading="billing" @click="confirmCreateBill">
            Registrar Factura Recibida
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <!-- Dialogo para Pago de Proveedor -->
    <v-dialog v-model="payDialog" max-width="480" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="headline success white--text py-4">
          <v-icon left dark class="mr-2">mdi-cash-check</v-icon>
          Registrar Pago Proveedor
          <v-spacer></v-spacer>
          <v-btn icon dark @click="payDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model.number="payForm.amount" type="number" min="0" step="0.01" label="Importe a pagar" outlined dense />
            </v-col>
            <v-col cols="12">
              <v-select v-model="payForm.paymentMethod" :items="['Efectivo', 'Transferencia', 'Cheque', 'Banco']" label="Medio de pago" outlined dense />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="payForm.paymentDate" type="date" label="Fecha de pago" outlined dense />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="payForm.notes" label="Observaciones" outlined dense rows="2" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="payDialog = false" class="mr-2">Cancelar</v-btn>
          <v-btn color="success" dark class="px-6 rounded-lg font-weight-bold" :loading="paying" @click="confirmPaySupplier">
            Registrar Pago
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="confirming" z-index="9999">
      <div class="text-center">
        <v-progress-circular indeterminate size="64" color="white" class="mb-4"></v-progress-circular>
        <div class="text-h6 font-weight-bold white--text">Procesando confirmación...</div>
        <div class="text-body-2 white--text">Por favor, espere.</div>
      </div>
    </v-overlay>

    <v-snackbar
      v-model="snackbar.show"
      top
      elevation="6"
      timeout="4000"
      :class="`premium-toast premium-toast--${snackbar.color}`"
    >
      <span class="d-flex align-center justify-center white--text">
        <v-icon left color="white" class="mr-2">
          {{ snackbar.color === 'error' ? 'mdi-alert-circle' : (snackbar.color === 'warning' ? 'mdi-alert' : 'mdi-check-circle') }}
        </v-icon>
        {{ snackbar.text }}
      </span>
    </v-snackbar>
  </v-container>
</template>

<script>
import { purchasesService } from "../services/purchases";
import { currentAccountsService } from "../services/currentAccounts";
import { productsService } from "../services/products";
import { warehousesService } from "../services/warehouses";
import HelpHint from "@/components/HelpHint.vue";

export default {
  name: "PurchasesManagement",
  components: { HelpHint },
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      viewOrderDialog: false,
      editingOrderId: null,
      cancelling: false,
      confirming: false,
      receiveDialog: false,
      receiving: false,
      receiveFormItems: [],
      receivePartialReason: "",
      orders: [],
      suppliers: [],
      products: [],
      filteredProducts: [],
      loadingProducts: false,
      warehouses: [],
      selectedOrder: null,
      snackbar: { show: false, text: "", color: "success" },
      billDialog: false,
      billing: false,
      payDialog: false,
      paying: false,
      payForm: {
        amount: 0,
        paymentMethod: "Efectivo",
        paymentDate: new Date().toISOString().split('T')[0],
        notes: ""
      },
      billForm: {
        invoiceType: "A",
        supplierDocNumber: "",
        issueDate: new Date().toISOString().split('T')[0],
        notes: "",
        attachment: null
      },
      newOrder: {
        supplierId: "",
        warehouseId: "DEFAULT_WH",
        items: []
      },
      itemForm: {
        productId: "",
        quantity: 1,
        unitCost: 0
      },
      headers: [
        { text: "Número", value: "docNumber", class: "font-weight-bold" },
        { text: "Fecha", value: "issueDate" },
        { text: "Proveedor", value: "supplierAccount.name" },
        { text: "Total", value: "total", align: "right" },
        { text: "Estado", value: "status", align: "center" }
      ],
      itemHeaders: [
        { text: "Producto", value: "productName" },
        { text: "Cantidad", value: "quantity", align: "center" },
        { text: "Costo Unit.", value: "unitCost", align: "right" },
        { text: "Total", value: "lineTotal", align: "right" },
        { text: "", value: "actions", sortable: false, align: "right" }
      ]
    };
  },
  computed: {
    newOrderTotal() {
      return this.newOrder.items.reduce((acc, item) => acc + (item.quantity * item.unitCost), 0);
    },
    isOrderValid() {
      return this.newOrder.supplierId && this.newOrder.items.length > 0;
    },
    hasReceiveShortage() {
      return this.receiveFormItems.some((item) => Number(item.receivedQty || 0) < Number(item.pendingQty || 0));
    }
  },
  async created() {
    await this.loadInitialData();
  },
  methods: {
    parseError(err) {
      return err?.response?.data?.errors || err?.response?.data?.error || err?.response?.data?.message || err?.message || "Error inesperado";
    },
    viewAttachment(id) {
      window.open(commercialDocumentsService.getPurchaseAttachmentUrl(id), '_blank');
    },
    async loadInitialData() {
      this.loading = true;
      try {
        const [ordersRes, suppliersRes, productsRes, warehousesRes] = await Promise.all([
          purchasesService.list(),
          currentAccountsService.list({ accountType: "supplier" }),
          productsService.list(""),
          warehousesService.list()
        ]);
        this.orders = ordersRes.data.data || [];
        this.suppliers = (suppliersRes.data.data || suppliersRes.data) || [];
        this.products = (productsRes.data.data || productsRes.data) || [];
        this.warehouses = (warehousesRes.data.data || warehousesRes.data) || [];
      } catch (e) {
        this.showSnackbar(this.parseError(e), "error");
      } finally {
        this.loading = false;
      }
    },
    async selectOrder(item) {
      this.selectedOrder = item;
      this.viewOrderDialog = true;
      try {
        const res = await commercialDocumentsService.getPurchase(item.id);
        if (res.data && res.data.data) {
          this.selectedOrder = res.data.data;
        }
      } catch (e) {
        console.error("Error fetching order details", e);
      }
    },
    createNewOrder() {
      this.editingOrderId = null;
      this.newOrder = { supplierId: "", warehouseId: "DEFAULT_WH", items: [] };
      this.dialog = true;
    },
    addItem() {
      if (!this.itemForm.productId) {
        this.showSnackbar("Por favor, seleccione un producto.", "warning");
        return;
      }
      if (this.itemForm.quantity <= 0) {
        this.showSnackbar("La cantidad debe ser mayor a 0.", "warning");
        return;
      }
      if (this.itemForm.unitCost <= 0) {
        this.showSnackbar("Debe ingresar un costo unitario mayor a 0.", "warning");
        return;
      }

      const prod = this.filteredProducts.find(x => x.id === this.itemForm.productId);
      this.newOrder.items.push({ ...this.itemForm, productName: prod ? prod.name : null });
      this.itemForm = { productId: "", quantity: 1, unitCost: 0 };
    },
    getProductName(id) {
      const p = this.products.find(x => x.id === id) || this.filteredProducts.find(x => x.id === id);
      return p ? p.name : id;
    },
    async saveOrder() {
      this.saving = true;
      try {
        if (this.editingOrderId) {
          await commercialDocumentsService.updatePurchase(this.editingOrderId, {
            supplierAccountId: this.newOrder.supplierId,
            warehouseId: this.newOrder.warehouseId,
            items: this.newOrder.items,
            updatedBy: "User"
          });
          this.showSnackbar("Orden actualizada correctamente");
        } else {
          await purchasesService.create({
            ...this.newOrder,
            actor: "User"
          });
          this.showSnackbar("Orden de compra creada correctamente");
        }
        this.dialog = false;
        await this.loadInitialData();
        
        if (this.editingOrderId && this.selectedOrder && this.selectedOrder.id === this.editingOrderId) {
           this.selectedOrder = this.orders.find(o => o.id === this.editingOrderId);
        }
      } catch (e) {
        this.showSnackbar(this.parseError(e), "error");
      } finally {
        this.saving = false;
      }
    },
    editOrder(order) {
      this.editingOrderId = order.id;
      this.newOrder = {
        supplierId: order.supplierAccountId,
        warehouseId: order.warehouseId || "DEFAULT_WH",
        items: (order.items || []).map(item => ({
          productId: item.productId,
          productName: item.product?.name || this.getProductName(item.productId),
          quantity: Number(item.quantity || 0),
          unitCost: Number(item.unitCost || 0)
        }))
      };
      this.viewOrderDialog = false;
      this.dialog = true;
    },
    async cancelOrder(id) {
      if (!confirm("¿Está seguro que desea cancelar esta orden?")) return;
      this.cancelling = true;
      try {
        await commercialDocumentsService.updatePurchase(id, {
          status: 'cancelled',
          updatedBy: "User"
        });
        this.showSnackbar("Orden cancelada");
        this.viewOrderDialog = false;
        await this.loadInitialData();
      } catch (e) {
        this.showSnackbar(this.parseError(e), "error");
      } finally {
        this.cancelling = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString("es-AR", { 
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute:'2-digit' 
      });
    },
    async confirmOrder(id) {
      this.confirming = true;
      try {
        await purchasesService.confirm(id);
        this.showSnackbar("Orden confirmada");
        await this.loadInitialData();
        // Update selected order view
        this.selectedOrder = this.orders.find(o => o.id === id);
      } catch (e) {
        this.showSnackbar(this.parseError(e), "error");
      } finally {
        this.confirming = false;
      }
    },
    openReceiveDialog(order) {
      this.receivePartialReason = "";
      this.receiveFormItems = (order.items || []).map(item => {
        const orderedQty = Number(item.quantity || 0);
        const alreadyReceivedQty = Number(item.receivedQuantity || 0);
        const pendingQty = Math.max(0, orderedQty - alreadyReceivedQty);
        return {
          purchaseItemId: item.id,
          productName: item.product?.name || this.getProductName(item.productId),
          orderedQty,
          alreadyReceivedQty,
          pendingQty,
          receivedQty: pendingQty
        };
      });
      this.receiveDialog = true;
    },
    async confirmReceiveProducts() {
      if (!this.selectedOrder) return;
      this.receiving = true;
      try {
        const id = this.selectedOrder.id;
        if (this.hasReceiveShortage && !String(this.receivePartialReason || "").trim()) {
          this.showSnackbar("Ingresá el motivo de la recepción parcial.", "warning");
          this.receiving = false;
          return;
        }
        const invalidItem = this.receiveFormItems.find(x => Number(x.receivedQty || 0) < 0 || Number(x.receivedQty || 0) > Number(x.pendingQty || 0));
        if (invalidItem) {
          this.showSnackbar("La cantidad recibida no puede superar lo pendiente.", "warning");
          this.receiving = false;
          return;
        }
        const items = this.receiveFormItems.map(x => ({
          purchaseItemId: x.purchaseItemId,
          receivedQuantity: Number(x.receivedQty || 0)
        }));

        const response = await purchasesService.receive(id, "User", items, this.receivePartialReason);
        const message = response.data?.data?.message || response.data?.message || "Mercadería recibida y stock actualizado";
        this.showSnackbar(message);
        this.receiveDialog = false;
        await this.loadInitialData();
        this.selectedOrder = this.orders.find(o => o.id === id);
      } catch (e) {
        this.showSnackbar(this.parseError(e), "error");
      } finally {
        this.receiving = false;
      }
    },
    createBill(id) {
      const order = this.orders.find(o => o.id === id);
      if (!order) return;
      this.billForm = {
        invoiceType: "A",
        supplierDocNumber: "",
        issueDate: new Date().toISOString().split('T')[0],
        notes: `Factura asociada a la Orden de Compra ${order.docNumber}`,
        attachment: null
      };
      this.billDialog = true;
    },
    async confirmCreateBill() {
      if (!this.$refs.billForm.validate()) return;
      if (!this.selectedOrder) return;
      this.billing = true;
      try {
        const order = this.selectedOrder;
        const items = (order.items || []).map(item => ({
          purchaseItemId: item.id,
          billedQuantity: Number(item.receivedQuantity || item.quantity || 0)
        }));

        let attachmentPath = null;
        if (this.billForm.attachment) {
          try {
            const uploadRes = await purchasesService.uploadAttachment(order.id, this.billForm.attachment);
            attachmentPath = uploadRes.data?.data?.path || null;
          } catch (uploadErr) {
            console.error("Error al subir archivo", uploadErr);
            this.showSnackbar("La factura se registrará, pero falló la subida del archivo adjunto.", "warning");
          }
        }

        await purchasesService.registerSupplierInvoice(order.id, {
          invoiceType: this.billForm.invoiceType,
          supplierInvoiceNumber: this.billForm.supplierDocNumber,
          supplierInvoiceDate: this.billForm.issueDate,
          items,
          attachmentPath,
          notes: this.billForm.notes,
          actor: "User"
        });

        this.showSnackbar("Factura de Proveedor registrada y contabilizada exitosamente.");
        this.billDialog = false;
        await this.loadInitialData();
        this.selectedOrder = this.orders.find(o => o.id === order.id);
      } catch (e) {
        this.showSnackbar("Error al registrar factura: " + this.parseError(e), "error");
      } finally {
        this.billing = false;
      }
    },
    openPayDialog(order) {
      this.selectedOrder = order;
      const pending = Number(order.total || 0) - Number(order.paidAmount || 0);
      this.payForm = {
        amount: Math.max(0, Number(pending.toFixed(2))),
        paymentMethod: "Efectivo",
        paymentDate: new Date().toISOString().split('T')[0],
        notes: `Pago proveedor ${order.supplierInvoiceNumber || order.docNumber}`
      };
      this.payDialog = true;
    },
    async confirmPaySupplier() {
      if (!this.selectedOrder) return;
      this.paying = true;
      try {
        const res = await purchasesService.paySupplierInvoice(this.selectedOrder.id, {
          amount: Number(this.payForm.amount || 0),
          paymentMethod: this.payForm.paymentMethod,
          paymentDate: this.payForm.paymentDate,
          notes: this.payForm.notes,
          actor: "User"
        });
        const message = res.data?.data?.message || "Pago proveedor registrado.";
        this.showSnackbar(message);
        this.payDialog = false;
        await this.loadInitialData();
        this.selectedOrder = this.orders.find(o => o.id === this.selectedOrder.id);
      } catch (e) {
        this.showSnackbar("Error al registrar pago: " + this.parseError(e), "error");
      } finally {
        this.paying = false;
      }
    },
    getStatusColor(status) {
      const colors = {
        draft: "grey darken-1",
        purchase: "info",
        partial: "warning",
        done: "success",
        invoiced: "teal",
        cancelled: "error",
        issued: "blue-grey",
        paid: "green darken-2"
      };
      return colors[status] || "grey";
    },
    getStatusText(status) {
      const texts = {
        draft: "Borrador",
        purchase: "Pedido de Compra",
        partial: "Recepción parcial",
        done: "Recibido",
        invoiced: "Facturado",
        cancelled: "Cancelado",
        issued: "Emitido",
        paid: "Pagado"
      };
      return texts[status] || status;
    },
    formatNumber(val) {
      return Number(val || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    showSnackbar(text, color = "success") {
      this.snackbar = { show: true, text, color };
    }
  },
  watch: {
    "newOrder.supplierId": {
      async handler(newSupplierId) {
        this.itemForm.productId = "";
        if (!newSupplierId) {
          this.filteredProducts = [];
          return;
        }
        this.loadingProducts = true;
        try {
          const res = await productsService.list("", { supplierId: newSupplierId });
          this.filteredProducts = res.data.data || res.data || [];
        } catch (e) {
          this.showSnackbar("Error al cargar productos del proveedor: " + this.parseError(e), "error");
        } finally {
          this.loadingProducts = false;
        }
      }
    },
    "itemForm.productId": {
      handler(newProductId) {
        if (newProductId) {
          const prod = this.filteredProducts.find(x => x.id === newProductId);
          if (prod) {
            this.itemForm.unitCost = Number(prod.standardCost || 0);
          }
        } else {
          this.itemForm.unitCost = 0;
        }
      }
    }
  }
};
</script>

<style scoped>
.page-container {
  background-color: #f8fafc;
  min-height: 100vh;
  padding: 24px;
}
.header-section {
  display: flex;
  align-items: center;
}
.custom-table >>> th {
  background-color: #f1f5f9 !important;
  font-weight: bold !important;
  color: #475569 !important;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
}
.bg-light-grey {
  background-color: #f1f5f9;
}
.sticky-card {
  position: sticky;
  top: 88px;
}
</style>
